import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, ExternalLink } from 'lucide-react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomerMailers } from '@/modules/Customer/services'
import type { CustomerMailer, MailerType } from '@/modules/Customer/types'

interface Props {
    customerId?: number
    // Currently-bound service. When set, the preview shows the mailer
    // whose `service_ids` contains it (the exact group the worker will
    // resolve at send time) instead of just the first service.report
    // mailer — critical for customers with >1 report mailer.
    serviceId?: number | null
}

// Read-only surface for the recipient list. After plan 12 P1 the V2
// worker pulls recipients from the customer's `service.report`-scoped
// mailer at send time, so the report form no longer owns an emails
// field. This block renders the current mailer's to/cc/bcc chips (if
// any) plus a deep-link to the customer's Mailer editor. Empty state
// tells the admin that no mail will be sent until a mailer is set up.
const ServiceReportMailerHint = ({ customerId, serviceId }: Props) => {
    const { t } = useTranslation('report_waf')

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, 'service-report-mailer', customerId],
        queryFn: () => getCustomerMailers({ customerID: customerId! }),
        enabled: !!customerId && customerId !== 0,
    })

    const mailer = useMemo<CustomerMailer | undefined>(() => {
        const reportMailers = (data?.results || []).filter((m) =>
            m.mailer_types?.some(
                (mt: MailerType) => mt.mailer_scope === 'service.report'
            )
        )
        // Prefer the mailer bound via `service_id` (what the worker
        // actually resolves). Fall back to the first only when unbound —
        // mirrors the backend `.first` behaviour for legacy reports.
        if (serviceId != null) {
            const matched = reportMailers.find((m) =>
                (m.service_ids || []).some((id) => Number(id) === serviceId)
            )
            if (matched) return matched
        }
        return reportMailers[0]
    }, [data, serviceId])

    const split = (csv?: string | null) =>
        (csv || '')
            .split(/[,;]\s*/)
            .map((e) => e.trim())
            .filter(Boolean)

    const to = split(mailer?.mailer_receiver)
    const cc = split(mailer?.mailer_cc)
    const bcc = split(mailer?.mailer_bcc)
    const manageHref = customerId
        ? `/customers/${customerId}?tab=mailers`
        : undefined

    return (
        <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-sm font-medium">
                    <Mail className="w-3.5 h-3.5" />
                    {t('fields.recipients')}
                </label>
                {manageHref && (
                    <a
                        href={manageHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                        {t('recipients_manage_link', {
                            defaultValue: 'Manage at Customer > Mailers',
                        })}
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </div>

            {!customerId ? (
                <p className="text-xs text-muted-foreground">
                    {t('recipients_select_customer', {
                        defaultValue:
                            'Pick a customer first — recipients come from their service.report mailer.',
                    })}
                </p>
            ) : !mailer ? (
                <p className="text-xs text-muted-foreground">
                    {t('recipients_no_mailer', {
                        defaultValue:
                            'This customer has no service.report mailer configured — the weekly email will not be delivered until one is created.',
                    })}
                </p>
            ) : (
                <div className="space-y-1.5">
                    {to.length > 0 && (
                        <ChipRow label="To" emails={to} variant="primary" />
                    )}
                    {cc.length > 0 && (
                        <ChipRow label="Cc" emails={cc} variant="muted" />
                    )}
                    {bcc.length > 0 && (
                        <ChipRow label="Bcc" emails={bcc} variant="muted" />
                    )}
                    {to.length === 0 && cc.length === 0 && bcc.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                            {t('recipients_mailer_empty', {
                                defaultValue:
                                    'service.report mailer has no receivers configured.',
                            })}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

const ChipRow = ({
    label,
    emails,
    variant,
}: {
    label: string
    emails: string[]
    variant: 'primary' | 'muted'
}) => (
    <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground shrink-0 w-8">
            {label}:
        </span>
        {emails.map((email) => (
            <span
                key={email}
                className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs ${
                    variant === 'primary'
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'bg-muted text-muted-foreground border border-border'
                }`}
            >
                {email}
            </span>
        ))}
    </div>
)

export default ServiceReportMailerHint
